import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { debounce } from '@mobi/utils/functions/debounce'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import {
  getFollowMeSharedBetsFeed,
  type FollowMeFeedParams,
} from '@core/Areas/FollowMe/helpers/api'
import { ShareFeedPageSize } from '../constants'
import { useSharedBetsTopicSubscription } from './useSharedBetsTopicSubscription'

export const useSharedBetsData = ({
  sortOrder,
  sortFilter,
  isFollowing = false,
  accountId = null
}: Pick<FollowMeFeedParams, 'isFollowing' | 'sortOrder' | 'sortFilter' | 'accountId'>) => {
  useSharedBetsTopicSubscription()
  const { data, isLoading, refetch, isRefetching, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: isFollowing
        ? [...queryKeys.followMeFeedFollowing, sortOrder, sortFilter]
        : [...queryKeys.followMeFeedExplore, sortOrder, sortFilter],
      queryFn: ({ pageParam }: { pageParam: number | null }) =>
        getFollowMeSharedBetsFeed({
          pageParam,
          sortOrder,
          sortFilter,
          isFollowing,
          accountId,
        }),

      initialPageParam: null,

      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.length < Number(ShareFeedPageSize)) return undefined
        return lastPage[lastPage.length - 1]?.postId ?? undefined
      },

      staleTime: 1000 * 60 * 2,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

  const isEmptyState = data?.pages[0].length === 0
  const isFinalPage = data && data.pages[data.pages.length - 1].length < ShareFeedPageSize

  const pages = data?.pages ?? []
  const firstPostSeenId = pages?.[0]?.[0]?.postId ?? null

  return {
    data,
    isLoading,
    refetch,
    isRefetching,
    firstPostSeenId,
    /** Automatically triggers a fetch for next page when in view */
    LoadMoreButton: () =>
      isFinalPage || isEmptyState ? null : (
        <LoadMoreButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
      ),
  }
}

// ===============
// Local Component
// ===============

const LoadMoreButton: React.FC<{
  onClick: () => void
  isFetching: boolean
}> = ({ onClick, isFetching }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const containerEl = wrapperRef.current
    if (!containerEl || isFetching) return
    const fetchNextPage = debounce(onClick, 300)
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchNextPage()
    })
    observer.observe(containerEl)
    return () => observer.disconnect()
  }, [wrapperRef, onClick, isFetching])

  return (
    <div ref={wrapperRef}>
      <Button
        color='secondary_outline'
        size='sm'
        onClick={onClick}
        isFullWidth
        isLoading={isFetching}
      >
        Load More
      </Button>
    </div>
  )
}
